import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { transformResponseHandler } from 'utils/transformResponseHandler';

export const cashManagementApi = createApi({
    reducerPath: 'cashManagementApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.REACT_APP_API_ROUTE}`,
        prepareHeaders: (headers, { getState }) => {
            const token = getState()?.posData?.token;
            if (token) {
                headers.set('Authorization', token);
            }
            return headers;
        },
    }),
    refetchOnMountOrArgChange: true,
    keepUnusedDataFor: 86400,
    tagTypes: ['RunningShifts', 'CashHistory', 'CashReports', 'StaffList', 'WalletBalance', 'WalletTransactions'],
    endpoints: (builder) => ({
        // Tab 1: Get Running Shifts
        getRunningShifts: builder.query({
            query: ({ page = 1, limit = 10, search = '' } = {}) => {
                const params = { page, limit };
                if (search && search.trim()) {
                    params.search = search.trim();
                }
                return {
                    url: '/pos/cash-management/running-shifts',
                    method: 'GET',
                    params,
                };
            },
            providesTags: ['RunningShifts'],
            transformErrorResponse: (error) => transformResponseHandler(error),
        }),

        // Staff list dropdown for Tab 2 & Tab 3
        getStaffList: builder.query({
            query: () => ({
                url: '/pos/cash-management/staff-list',
                method: 'GET',
            }),
            providesTags: ['StaffList'],
            transformErrorResponse: (error) => transformResponseHandler(error),
        }),

        // Tab 2: Get Shift History
        getCashHistory: builder.query({
            query: ({ status = 'all', staff_id = 'all', page = 1, perPage = 10, search = '', startDate = '', endDate = '' } = {}) => {
                const params = { status, staff_id, page, perPage };
                if (search && search.trim()) {
                    params.search = search.trim();
                }
                if (startDate) {
                    params.startDate = startDate;
                }
                if (endDate) {
                    params.endDate = endDate;
                }
                return {
                    url: '/pos/cash-management/all-history',
                    method: 'GET',
                    params,
                };
            },
            providesTags: ['CashHistory'],
            transformErrorResponse: (error) => transformResponseHandler(error),
        }),

        // Tab 3: Get Cash Reports & Staff Variance Summary
        getCashReports: builder.query({
            query: ({ startDate = '', endDate = '', staff_id = 'all' } = {}) => {
                const params = {};
                if (staff_id && staff_id !== 'all') {
                    params.staff_id = staff_id;
                }
                if (startDate) {
                    params.startDate = startDate;
                }
                if (endDate) {
                    params.endDate = endDate;
                }
                return {
                    url: '/pos/cash-management/reports',
                    method: 'GET',
                    params,
                };
            },
            providesTags: ['CashReports'],
            transformErrorResponse: (error) => transformResponseHandler(error),
        }),

        // Shift Audit Report / Details (for View & Print)
        getShiftReport: builder.query({
            query: (id) => ({
                url: `/pos/cash-management/shift/${id}`,
                method: 'GET',
            }),
            transformErrorResponse: (error) => transformResponseHandler(error),
        }),

        // ── RESTAURANT CASH WALLET ENDPOINTS ──────────────────
        // 1. Get Wallet Balance
        getWalletBalance: builder.query({
            query: () => ({
                url: '/pos/cash-management/wallet/balance',
                method: 'GET',
            }),
            providesTags: ['WalletBalance'],
            transformErrorResponse: (error) => transformResponseHandler(error),
        }),

        // 2. Adjust Wallet Balance (direct or incremental)
        adjustWalletBalance: builder.mutation({
            query: (body) => ({
                url: '/pos/cash-management/wallet/adjust',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['WalletBalance', 'WalletTransactions'],
            transformErrorResponse: (error) => transformResponseHandler(error),
        }),

        // 3. Get Wallet Audit Ledger Transactions
        getWalletTransactions: builder.query({
            query: ({ page = 1, limit = 10, type = 'all', direction = 'all', date_from = '', date_to = '', search = '' } = {}) => {
                const params = { page, limit };
                if (type && type !== 'all') params.type = type;
                if (direction && direction !== 'all') params.direction = direction;
                if (date_from) params.date_from = date_from;
                if (date_to) params.date_to = date_to;
                if (search && search.trim()) params.search = search.trim();
                return {
                    url: '/pos/cash-management/wallet/transactions',
                    method: 'GET',
                    params,
                };
            },
            providesTags: ['WalletTransactions'],
            transformErrorResponse: (error) => transformResponseHandler(error),
        }),
    }),
});

export const {
    useGetRunningShiftsQuery,
    useGetStaffListQuery,
    useGetCashHistoryQuery,
    useGetCashReportsQuery,
    useGetShiftReportQuery,
    useGetWalletBalanceQuery,
    useAdjustWalletBalanceMutation,
    useGetWalletTransactionsQuery,
} = cashManagementApi;
